import { FC } from "react";
import styled, { css, keyframes } from "styled-components";
import { formatDeadline, getDaysLeft, getUrgency, URGENCY_COLORS } from "../../helper/urgency";

/**
 * Shared participant-app kit: card surface, chips, pills and the "all done" mark.
 *
 * What the THEME decides here (tokens.ts): the card fill is the surface color;
 * tonal accents (name chip, tonal pill) use the brand ink derived from the
 * chrome color; the one main action per screen uses the secondary color; corner
 * sizes and the card edge come from the shape family that goes with the header
 * edge (--app-radius-*, --app-card-line, --app-shadow-*). What it never decides:
 * status colors (deadline urgency, the done check) — they must mean the same thing
 * in every study.
 */

/** Shape of react-i18next's t() we rely on (avoids version-coupled TFunction generics). */
type Translate = (key: string, options?: Record<string, unknown>) => string;

const INK_RGB = "var(--app-color-ink-rgb, var(--app-color-primary-rgb, 37, 99, 235))";
/** Brand ink (the chrome color made readable on light grounds). */
export const INK = "var(--app-color-ink, var(--app-color-primary, #2563eb))";
/** Brand ink wash at the given opacity. */
export const inkWash = (alpha: number): string => `rgba(${INK_RGB}, ${alpha})`;

/** Card surface: surface color, shape-family corners, tinted shadow, and a thin
 *  line only when the card would otherwise blend into the page. */
export const kitCard = css`
  background: var(--app-color-surface, #ffffff);
  border-radius: var(--app-radius-card, 24px);
  box-shadow:
    inset 0 0 0 1px var(--app-card-line, transparent),
    var(
      --app-shadow-card,
      0 1px 2px rgba(15, 23, 42, 0.04),
      0 16px 34px -24px rgba(15, 23, 42, 0.3)
    );
`;

/** "E2" for «Enfant 2», "E" for «Emma». */
export const initialsOf = (label: string): string => {
  const first = label.trim().charAt(0);
  const number = label.match(/\d+/)?.[0] ?? "";
  return `${first}${number}`.toUpperCase().slice(0, 3);
};

/**
 * Whom a questionnaire is about («Emma» / «Enfant 2»). Only rendered when the
 * questionnaire is about someone else: the API sends no label when the person
 * answers about themselves, so a self-only study never shows one.
 */
export const NameChip: FC<{ label: string; className?: string }> = ({ label, className }) => (
  <NameChipWrap className={className}>
    <NameAvatar aria-hidden="true">{initialsOf(label)}</NameAvatar>
    <NameText>{label}</NameText>
  </NameChipWrap>
);

const NameChipWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  height: 28px;
  padding: 0 11px 0 3px;
  box-sizing: border-box;
  border-radius: var(--app-radius-chip, 999px);
  background: ${inkWash(0.1)};
  color: var(--app-color-text, #0f172a);
  font-size: 13px;
  font-weight: 700;
`;

const NameAvatar = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${INK};
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const NameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Deadline as a dot + words, never a filled chip (same urgency scale as Suivi:
 * grey → amber from J-3 → red the last day). Within the week the weekday makes
 * "J-3" concrete; further out, the date does.
 */
export const DeadlineChip: FC<{ deadline: string; t: Translate; language: string }> = ({
  deadline,
  t,
  language,
}) => {
  const colors = URGENCY_COLORS[getUrgency(deadline)];
  const daysLeft = getDaysLeft(deadline);
  const date = new Date(deadline);
  const relative = formatDeadline(t, deadline);
  let detail = "";
  try {
    if (daysLeft >= 2 && daysLeft <= 6) {
      detail = new Intl.DateTimeFormat(language, { weekday: "long" }).format(date);
    } else if (daysLeft > 6) {
      detail = new Intl.DateTimeFormat(language, { day: "numeric", month: "short" }).format(date);
    }
  } catch {
    detail = "";
  }
  let longDate = "";
  try {
    longDate = new Intl.DateTimeFormat(language, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(date);
  } catch {
    longDate = date.toLocaleDateString();
  }

  return (
    <DueWrap $text={colors.text} aria-label={t("calendar_due_before", { date: longDate })}>
      <DueDot $dot={colors.dot} $halo={colors.halo} aria-hidden="true" />
      <span aria-hidden="true">{detail ? `${relative} · ${detail}` : relative}</span>
    </DueWrap>
  );
};

const DueWrap = styled.span<{ $text: string }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $text }) => $text};
  white-space: nowrap;
`;

const DueDot = styled.i<{ $dot: string; $halo: string }>`
  flex: none;
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $dot }) => $dot};
  box-shadow: 0 0 0 3px ${({ $halo }) => $halo};
`;

const pillBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 0 18px;
  box-sizing: border-box;
  border: none;
  border-radius: var(--app-radius-pill, 999px);
  font-family: var(--app-font-heading, inherit);
  font-size: 15.5px;
  font-weight: var(--app-font-heading-weight, 700);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  ion-icon {
    flex: none;
    font-size: 18px;
  }
`;

/** The ONE main action of a screen (secondary color). */
export const MainPill = styled.span`
  ${pillBase}
  background: var(--app-color-secondary, #f59e0b);
  color: var(--app-color-secondary-contrast, #041723);
  box-shadow: 0 10px 18px -12px rgba(var(--app-color-secondary-rgb, 245, 158, 11), 0.95);
`;

/** A quieter action next to or below the main one (brand-ink wash). */
export const TonalPill = styled.span`
  ${pillBase}
  background: ${inkWash(0.1)};
  color: ${INK};
`;

const markPop = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const markGlow = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const markDraw = keyframes`
  from { stroke-dashoffset: 21; }
  to { stroke-dashoffset: 0; }
`;

/** "Tout est à jour": a green check, the same in every study (no logo, no art). */
export const DoneMark: FC = () => (
  <DoneWrap aria-hidden="true">
    <DoneGlow />
    <DoneDisc>
      <svg
        width="54"
        height="54"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12.5l4.5 4.5L19 7.5" />
      </svg>
    </DoneDisc>
  </DoneWrap>
);

const DoneWrap = styled.span`
  position: relative;
  display: block;
  width: 168px;
  height: 168px;
  flex: none;
`;

const DoneGlow = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(
    closest-side,
    rgba(22, 163, 74, 0.2),
    rgba(22, 163, 74, 0.07) 62%,
    rgba(22, 163, 74, 0) 100%
  );
  animation: ${markGlow} 0.9s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DoneDisc = styled.span`
  position: absolute;
  left: 36px;
  top: 36px;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #4ade80 0%, #16a34a 100%);
  box-shadow:
    0 20px 34px -18px rgba(22, 163, 74, 0.85),
    inset 0 2px 0 rgba(255, 255, 255, 0.35);
  animation: ${markPop} 0.62s cubic-bezier(0.34, 1.56, 0.64, 1) both;

  path {
    stroke-dasharray: 21;
    stroke-dashoffset: 0;
    animation: ${markDraw} 0.42s 0.26s ease-out both;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    path {
      animation: none;
    }
  }
`;
